import { useState, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { LockKey, CheckCircle, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [pin, setPin] = useState(["", "", "", ""]);
  const [confirmPin, setConfirmPin] = useState(["", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const handlePinChange = useCallback((pins, setPins, index, value) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    
    setPins(prevPin => {
      const newPin = [...prevPin];
      newPin[index] = digit;
      return newPin;
    });

    // Auto-focus next input
    if (digit && index < 3) {
      const prefix = setPins === setPin ? 'new' : 'confirm';
      const nextInput = document.getElementById(`${prefix}-pin-${index + 1}`);
      if (nextInput) {
        nextInput.focus();
      }
    }
  }, []);

  const handlePinKeyDown = useCallback((pins, setPins, index, e) => {
    if (e.key === 'Backspace' && !pins[index] && index > 0) {
      const prefix = setPins === setPin ? 'new' : 'confirm';
      const prevInput = document.getElementById(`${prefix}-pin-${index - 1}`);
      if (prevInput) {
        prevInput.focus();
      }
    }
  }, []);

  const pinValue = useMemo(() => pin.join(''), [pin]);
  const confirmPinValue = useMemo(() => confirmPin.join(''), [confirmPin]);

  const handleSubmit = async () => {
    if (pinValue.length !== 4) {
      toast.error("Por favor, insere o novo PIN de 4 dígitos");
      return;
    }

    if (pinValue !== confirmPinValue) {
      toast.error("Os PINs não coincidem");
      return;
    }

    if (!token) {
      toast.error("Token inválido. Solicite um novo link de recuperação.");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_pin: pinValue
      });

      setSuccess(true);
      toast.success("Senha alterada com sucesso!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao redefinir senha");
    } finally {
      setLoading(false);
    }
  };

  // PIN input renderer
  const renderPinInput = (pins, setPins, prefix) => (
    <div className="flex justify-center gap-3">
      {[0, 1, 2, 3].map((index) => (
        <input
          key={`${prefix}-pin-input-${index}`}
          id={`${prefix}-pin-${index}`}
          type="tel"
          inputMode="numeric"
          maxLength={1}
          value={pins[index]}
          onChange={(e) => handlePinChange(pins, setPins, index, e.target.value)}
          onKeyDown={(e) => handlePinKeyDown(pins, setPins, index, e)}
          className="w-14 h-14 text-2xl text-center bg-accent text-white border-2 border-primary/30 rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
          autoComplete="off"
        />
      ))}
    </div>
  );

  if (!token) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <header className="bg-primary text-white p-4 flex items-center gap-4">
          <button 
            onClick={() => navigate("/login")}
            className="p-2 hover:bg-white/10 rounded-full transition-colors"
          >
            <ArrowLeft size={24} weight="bold" />
          </button>
          <h1 className="text-xl font-bold">Redefinir Senha</h1>
        </header>
        
        <main className="flex-1 flex flex-col items-center justify-center p-6">
          <div className="text-center">
            <div className="w-20 h-20 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <LockKey size={48} className="text-destructive" weight="duotone" />
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-2">Link Inválido</h2>
            <p className="text-muted-foreground mb-6">
              Este link de recuperação é inválido ou expirou.<br />
              Por favor, solicite um novo link.
            </p>
            <button
              onClick={() => navigate("/forgot-password")}
              className="btn-primary"
            >
              Solicitar Novo Link
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="bg-primary text-white p-4 flex items-center gap-4">
        <button 
          onClick={() => navigate("/login")}
          className="p-2 hover:bg-white/10 rounded-full transition-colors"
        >
          <ArrowLeft size={24} weight="bold" />
        </button>
        <h1 className="text-xl font-bold">Redefinir Senha</h1>
      </header>

      <main className="flex-1 flex flex-col items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md text-center"
        >
          {!success ? (
            <>
              <div className="mb-8">
                <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <LockKey size={48} className="text-primary" weight="duotone" />
                </div>
                <h2 className="text-2xl font-bold text-foreground">Criar Novo PIN</h2>
                <p className="text-muted-foreground mt-2">
                  Insere um novo PIN de 4 dígitos para a tua conta.
                </p>
              </div>

              <div className="space-y-6">
                <div>
                  <p className="text-sm text-muted-foreground mb-3">Novo PIN</p>
                  {renderPinInput(pin, setPin, 'new')}
                </div>

                <div>
                  <p className="text-sm text-muted-foreground mb-3">Confirmar PIN</p>
                  {renderPinInput(confirmPin, setConfirmPin, 'confirm')}
                </div>

                <button
                  onClick={handleSubmit}
                  disabled={loading || pinValue.length !== 4 || confirmPinValue.length !== 4}
                  className="btn-primary w-full disabled:opacity-50"
                >
                  {loading ? "A processar..." : "Confirmar Nova Senha"}
                </button>
              </div>
            </>
          ) : (
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
            >
              <div className="w-24 h-24 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={64} className="text-primary" weight="duotone" />
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-2">Senha Alterada!</h2>
              <p className="text-muted-foreground mb-6">
                A tua nova senha foi criada com sucesso.<br />
                Já podes fazer login com o novo PIN.
              </p>
              <button
                onClick={() => navigate("/login")}
                className="btn-primary w-full"
              >
                Ir para Login
              </button>
            </motion.div>
          )}
        </motion.div>
      </main>

      <footer className="p-4 text-center text-muted-foreground text-sm">
        Nzadi Fit © 2026 – Saúde em Angola 🇦🇴
      </footer>
    </div>
  );
};

export default ResetPassword;
